<template>
  <div class="absolute inset-0 z-20 bg-white rounded-2xl flex flex-col overflow-hidden">
    <!-- Header -->
    <div class="flex-shrink-0 px-4 py-3 bg-gradient-to-br from-teal-600 to-purple-500 text-white flex items-center gap-2">
      <button
        @click="$emit('close')"
        class="p-1 rounded-full hover:bg-white/20 transition-colors border-0 bg-transparent cursor-pointer text-white"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
      <span class="text-sm font-semibold truncate flex-1">{{ $t("ai.widget.report.formTitle") }}</span>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
      <p v-if="localData.hintText" class="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 leading-relaxed m-0">
        {{ localData.hintText }}
      </p>

      <div>
        <label class="block text-xs font-semibold text-gray-600 mb-1">
          {{ $t("ai.widget.report.titleLabel") }} <span class="text-red-400">*</span>
        </label>
        <input
          v-model="localData.title"
          type="text"
          class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
          :placeholder="$t('ai.widget.report.titlePlaceholder')"
        />
      </div>

      <div>
        <label class="block text-xs font-semibold text-gray-600 mb-1">
          {{ $t("ai.widget.report.emailLabel") }} <span class="text-red-400">*</span>
        </label>
        <input
          v-model="localData.email"
          type="email"
          class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
          placeholder="you@example.com"
        />
      </div>

      <div>
        <label class="block text-xs font-semibold text-gray-600 mb-1">{{ $t("ai.widget.report.languageLabel") }}</label>
        <select v-model="localData.language" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 bg-white">
          <option value="fr">Français</option>
          <option value="en">English</option>
        </select>
      </div>

      <div>
        <label class="block text-xs font-semibold text-gray-600 mb-1">{{ $t("ai.widget.report.periodTypeLabel") }}</label>
        <select v-model="localData.period_type" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 bg-white">
          <option value="day">{{ $t("daily") }}</option>
          <option value="week">{{ $t("weekly") }}</option>
          <option value="month">{{ $t("monthly") }}</option>
          <option value="custom">{{ $t("ai.widget.report.custom") }}</option>
        </select>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">{{ $t("ai.widget.report.periodStart") }}</label>
          <input v-model="localData.period_start" type="date" class="w-full border border-gray-300 rounded-md px-2 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400" />
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">{{ $t("ai.widget.report.periodEnd") }}</label>
          <input v-model="localData.period_end" type="date" class="w-full border border-gray-300 rounded-md px-2 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400" />
        </div>
      </div>

      <div>
        <label class="block text-xs font-semibold text-gray-600 mb-1">{{ $t("ai.widget.report.scheduleTypeLabel") }}</label>
        <select v-model="localData.schedule_type" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 bg-white">
          <option value="none">{{ $t("ai.widget.report.scheduleNone") }}</option>
          <option value="daily">{{ $t("daily") }}</option>
          <option value="weekly">{{ $t("weekly") }}</option>
          <option value="monthly">{{ $t("monthly") }}</option>
        </select>
      </div>

      <div v-if="localData.schedule_type !== 'none'" class="grid grid-cols-2 gap-2">
        <div v-if="localData.schedule_type === 'weekly'">
          <label class="block text-xs font-semibold text-gray-600 mb-1">{{ $t("ai.widget.report.dayOfWeek") }}</label>
          <select v-model="localData.schedule_day_of_week" class="w-full border border-gray-300 rounded-md px-2 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 bg-white">
            <option :value="1">{{ $t("ai.widget.report.monday") }}</option>
            <option :value="2">{{ $t("ai.widget.report.tuesday") }}</option>
            <option :value="3">{{ $t("ai.widget.report.wednesday") }}</option>
            <option :value="4">{{ $t("ai.widget.report.thursday") }}</option>
            <option :value="5">{{ $t("ai.widget.report.friday") }}</option>
            <option :value="6">{{ $t("ai.widget.report.saturday") }}</option>
            <option :value="7">{{ $t("ai.widget.report.sunday") }}</option>
          </select>
        </div>
        <div v-if="localData.schedule_type === 'monthly'">
          <label class="block text-xs font-semibold text-gray-600 mb-1">{{ $t("ai.widget.report.dayOfMonth") }}</label>
          <input v-model.number="localData.schedule_day_of_month" type="number" min="1" max="31" class="w-full border border-gray-300 rounded-md px-2 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400" />
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">{{ $t("ai.widget.report.scheduleTime") }}</label>
          <input v-model="localData.schedule_time" type="time" class="w-full border border-gray-300 rounded-md px-2 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400" />
        </div>
      </div>

      <div class="flex items-start gap-2.5 bg-gray-50 rounded-lg px-3 py-2.5">
        <input
          id="report-send-now"
          v-model="localData.send_now"
          type="checkbox"
          class="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer flex-shrink-0"
        />
        <label for="report-send-now" class="text-xs text-gray-700 cursor-pointer leading-snug">
          <span class="font-semibold block">{{ $t("ai.widget.report.sendNowLabel") }}</span>
          <span class="text-gray-500">
            {{ localData.schedule_type === "none" ? $t("ai.widget.report.sendNowHintOneTime") : $t("ai.widget.report.sendNowHintRecurring") }}
          </span>
        </label>
      </div>

      <p v-if="error" class="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 m-0">{{ error }}</p>
      <p v-if="success" class="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 m-0">
        {{ localData.send_now ? $t("ai.widget.report.successSentNow") : $t("ai.widget.report.success") }}
      </p>
    </div>

    <!-- Footer -->
    <div class="flex-shrink-0 px-4 py-3 border-t border-gray-100 flex gap-2">
      <button
        @click="$emit('close')"
        class="flex-1 border border-gray-300 rounded-lg py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors bg-white cursor-pointer"
      >
        {{ $t("ai.widget.report.cancel") }}
      </button>
      <button
        @click="$emit('submit', localData)"
        :disabled="submitting || success"
        :class="submitting || success ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'"
        class="flex-1 rounded-lg py-2 text-sm font-semibold text-white bg-gradient-to-br from-teal-600 to-purple-500 border-0 transition-opacity"
      >
        <template v-if="submitting">{{ $t("ai.widget.report.submitting") }}</template>
        <template v-else-if="localData.send_now">{{ $t("ai.widget.report.submitAndSend") }}</template>
        <template v-else>{{ $t("ai.widget.report.submit") }}</template>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "AiReportForm",

  props: {
    formData:   { type: Object,  required: true },
    submitting: { type: Boolean, default: false },
    error:      { type: String,  default: null },
    success:    { type: Boolean, default: false },
  },

  emits: ["close", "submit"],

  data() {
    return { localData: { ...this.formData } };
  },

  watch: {
    formData: { deep: true, handler(v) { this.localData = { ...v }; } },
  },
};
</script>
